import cn from 'clsx';
import React, { useState, useRef, useEffect } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useI18n } from '#src/hooks/i18n';
import { useBreakpoint } from '#src/hooks/use-breakpoint';
import {
  type Compare,
  type ParentOptions,
  type SelectionMode,
} from '#src/types';
import { buildLabelSuffix } from '#src/utils/export-data';
import { CompareForm } from './compare-form';

export function NestedDropdownMenu({
  label,
  parentId,
  selection,
  options,
  onChange,
  className,
  limits,
}: {
  label: string;
  options: ParentOptions;
  parentId: string | undefined;
  selection: Compare;
  className?: string;
  limits?: {
    [parentId: string]: {
      comparisonLimit: number;
      aggregationLimit: number;
    };
  };
  onChange: (state: {
    parentId: string;
    selectionMode: SelectionMode;
    selection: Compare;
  }) => void;
}) {
  const { t } = useI18n();
  const [selectedParent, setSelectedParent] = useState<string | null>(null);
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  const parentDropdownRef = useRef<HTMLDivElement>(null);
  const parentDropdownToggleRef = useRef<HTMLButtonElement>(null);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showParentDropdown) {
        setShowParentDropdown(false);
        setSelectedParent(null);
      }
    };

    if (showParentDropdown) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [showParentDropdown]);

  const handleLevelsDropdownToggle = (nextShow: boolean, event: any) => {
    if (
      event &&
      event.target &&
      parentDropdownToggleRef.current &&
      parentDropdownToggleRef.current.contains(event.target)
    ) {
      // Always allow toggling when clicking the toggle
      setShowParentDropdown(nextShow);
      setSelectedParent(null);
      return;
    }
    if (!nextShow) {
      if (
        event &&
        event.target &&
        parentDropdownRef.current &&
        parentDropdownRef.current.contains(event.target)
      ) {
        // Click inside dropdown menu, ignore close
        if (
          event.target.id === `second-level-dropdown-toggle-${selectedParent}`
        ) {
          setSelectedParent(null);
        }
        return;
      }
    }
    setShowParentDropdown(nextShow);
    setSelectedParent(null);
  };

  const parent = options.find((o) => o.id === parentId);
  const parentComparisonLimit =
    limits?.[parentId as string]?.comparisonLimit ??
    limits?.default?.comparisonLimit;
  const { suffix: labelSuffix, title: labelTitle } = buildLabelSuffix(
    parent,
    selection,
    parentComparisonLimit
  );

  const { isAboveLg } = useBreakpoint('lg');
  const menuDirection = isAboveLg ? 'right' : 'down';

  return (
    <Dropdown
      className={cn('nested-dropdown-menu h-100', className)}
      show={showParentDropdown}
      ref={parentDropdownRef}
      onToggle={handleLevelsDropdownToggle}
    >
      <Dropdown.Toggle
        variant='light'
        className='text-start w-100 h-100 d-flex justify-content-between align-items-center dropdown-toggle-border nested-dropdown-menu-toggle'
        ref={parentDropdownToggleRef}
      >
        <p
          className='nested-dropdown-toggle-title'
          title={`${label} ${parent?.name ?? ''} ${labelTitle ?? ''}`}
        >
          <span className='text-secondary mr-2'>{label}</span>
          <span className='fw-bold'>
            {parent?.name} {labelSuffix}
          </span>
        </p>
      </Dropdown.Toggle>
      <Dropdown.Menu className='w-100'>
        {options.map((parentItem) => {
          const { comparisonLimit, aggregationLimit } =
            limits?.[parentItem.id] ?? limits?.default ?? {};
          const defaultSelectionLabel =
            comparisonLimit != null && comparisonLimit !== 0
              ? `${t('filters.compare.top-items-label')} ${parentItem.shortName}`.replace(
                  '{count}',
                  comparisonLimit.toString()
                )
              : `${t('filters.compare.all-items-label')} ${parentItem.shortName}`;
          const showDefaultSelection = comparisonLimit !== 1;
          return (
            <Dropdown.Item
              as='button'
              key={parentItem.id + 'outer'}
              className={cn('pr-0 second-level-dropdown-item', {
                'is-open': selectedParent === parentItem.id,
              })}
              onClick={() => {
                if (parentItem.options == null) {
                  if (parentItem.id !== parentId) {
                    onChange({
                      parentId: parentItem.id,
                      selectionMode: 'default',
                      selection: [],
                    });
                  }
                  setShowParentDropdown(false);
                  setSelectedParent(null);
                  return;
                }
                setSelectedParent(parentItem.id);
              }}
            >
              {parentItem.options == null && (
                <span className='pr-4 small d-flex justify-content-between align-items-center'>
                  {parentItem.name}
                </span>
              )}
              {parentItem.options != null && (
                <Dropdown
                  drop={menuDirection}
                  className='h-100 flex-grow-1'
                  show={showParentDropdown && parentItem.id === selectedParent}
                >
                  <Dropdown.Toggle
                    as='span'
                    className='second-level-dropdown-toggle'
                    id={`second-level-dropdown-toggle-${parentItem.id}`}
                  >
                    <span>{parentItem.name}</span>
                  </Dropdown.Toggle>

                  <Dropdown.Menu className='second-level-dropdown-menu'>
                    {selectedParent === parentItem.id && (
                      <CompareForm
                        aggregationLimit={aggregationLimit}
                        comparisonLimit={comparisonLimit}
                        formId={parentItem.id}
                        shortName={parentItem.shortName}
                        label={`${t('filters.compare.label-prefix')} ${parentItem.shortName}`}
                        selection={parentId === parentItem.id ? selection : []}
                        defaultSelectionLabel={defaultSelectionLabel}
                        showDefaultSelection={showDefaultSelection}
                        options={parentItem.options}
                        onChange={(state) => {
                          onChange({
                            parentId: parentItem.id,
                            ...state,
                          });
                        }}
                        close={() => {
                          setShowParentDropdown(false);
                        }}
                      />
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Dropdown.Item>
          );
        })}
      </Dropdown.Menu>
    </Dropdown>
  );
}
